import * as THREE from 'three';
import {shouldHideArrowGroup} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';

/**
 * Helper function to prevent visibility when dragging
 * Returns false if dragging, otherwise returns the base visibility condition
 * @param {boolean} isDragging - Whether dragging is currently active
 * @param {boolean} baseVisibility - The base visibility condition
 * @return {boolean} Visibility state considering dragging
 */
export const preventVisibilityWhenDragging = (
    isDragging: boolean,
    baseVisibility: boolean
): boolean => {
    return !isDragging && baseVisibility;
};

/**
 * Helper function to set visibility on object and all children
 * @param {THREE.Object3D} obj - The object to set visibility on
 * @param {boolean} visible - Whether the object should be visible
 * @return {void}
 */
export const setObjectVisibility = (
    obj: THREE.Object3D,
    visible: boolean
): void => {
    obj.visible = visible;
    obj.traverse((child) => {
        child.visible = visible;
    });
};

/**
 * Helper function to dispose of Three.js object resources
 * Properly disposes geometries, materials, and textures
 * @param {THREE.Object3D} obj - The object to dispose resources for
 * @return {void}
 */
export const disposeObjectResources = (obj: THREE.Object3D): void => {
    obj.traverse((child) => {
        if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
            const geometry = child.geometry as THREE.BufferGeometry | undefined;
            if (geometry) {
                geometry.dispose();
            }
            const material = child.material as
                | THREE.Material
                | THREE.Material[]
                | undefined;
            if (Array.isArray(material)) {
                material.forEach((m) => {
                    if (m) {
                        m.dispose();
                    }
                });
            } else if (material) {
                material.dispose();
            }
        }
        if (child instanceof THREE.Sprite && child.material) {
            if (child.material.map) {
                child.material.map.dispose();
            }
            child.material.dispose();
        }
    });
};

/**
 * Arrow type identification result
 */
export type ArrowTypeResult = {
    type:
        | 'wallMeasurement'
        | 'cabinetDimension'
        | 'cabinetMovementArrows'
        | 'distanceWallMeasurement'
        | 'distanceWallMeasurementsDashed'
        | 'other';
    arrowId: string;
};

/**
 * Helper function to identify arrow types
 * @param {THREE.Group} arrow - The arrow group to identify
 * @return {ArrowTypeResult} The identified arrow type and ID
 */
export const getArrowType = (
    arrow: THREE.Group<THREE.Object3DEventMap>
): ArrowTypeResult => {
    const arrowId = arrow.userData.id as string;
    const arrowName = arrow.name;

    // Wall measurement arrows
    const wallMeasurementIds = [
        'arrowGroupTop',
        'arrowGroupFront',
        'arrowGroupLeft',
        'arrowGroupRight',
        'arrowGroupBack',
        'arrowGroupLeft-distance',
    ];
    if (wallMeasurementIds.includes(arrowId)) {
        return {type: 'wallMeasurement', arrowId};
    }

    // Cabinet dimension arrows
    if (arrowName === 'cabinetDimensionArrows') {
        return {type: 'cabinetDimension', arrowId};
    }

    // Distance wall measurements
    if (arrowName === 'distanceWallMeasurements') {
        return {type: 'distanceWallMeasurement', arrowId};
    }

    // Floor dashed wall-gap guides (same visibility rules as solid distance measurements)
    if (arrowName === 'distanceWallMeasurementsDashed') {
        return {type: 'distanceWallMeasurementsDashed', arrowId};
    }

    // Movement arrows (cabinetMovementArrows)
    if (arrowName === 'cabinetMovementArrows') {
        return {type: 'cabinetMovementArrows', arrowId};
    }

    return {type: 'other', arrowId};
};

const REPOSITION_OVERLAY_NAMES = new Set([
    'cabinetMovementArrows',
    'distanceWallMeasurements',
    'distanceWallMeasurementsDashed',
    'cabinetDimensionArrows',
]);

/**
 * Wall show/hide (camera orbit) only applies to room-dimension arrows.
 * Reposition arrows live in the same arrow list, and that pass was turning
 * them off whenever a wall faded. Movement mode owns their visibility.
 * @param {THREE.Object3D} arrow - An arrow mesh or group from arrowRefs
 * @return {boolean} True when this arrow should follow the hidden-wall list
 */
export const followsWallVisibility = (arrow: THREE.Object3D): boolean => {
    let node: THREE.Object3D | null = arrow;
    while (node) {
        if (REPOSITION_OVERLAY_NAMES.has(node.name)) {
            return false;
        }
        node = node.parent;
    }
    return true;
};

/**
 * Helper function to calculate visibility for different arrow types
 * @param {ArrowTypeResult} arrowType - The identified arrow type
 * @param {THREE.Group} arrow - The arrow group
 * @param {boolean} isDragging - Whether dragging is active
 * @param {boolean} showCabinets - Whether cabinets should be shown
 * @param {boolean} showArrows - Whether arrows should be shown (ruler state)
 * @param {CabinetMesh | null} selectedCabinetGroup - The currently selected cabinet group
 * @return {boolean} Whether the arrow should be visible
 */
export const calculateArrowVisibility = (
    arrowType: ArrowTypeResult,
    arrow: THREE.Group<THREE.Object3DEventMap>,
    isDragging: boolean,
    showCabinets: boolean,
    showArrows: boolean,
    selectedCabinetGroup: CabinetMesh | null
): boolean => {
    const {type, arrowId} = arrowType;

    if (!showCabinets) {
        return false;
    }

    // Hide all cabinet-related arrows when no cabinet is selected
    if (!selectedCabinetGroup) {
        if (
            type === 'cabinetDimension' ||
            type === 'cabinetMovementArrows' ||
            type === 'distanceWallMeasurement' ||
            type === 'distanceWallMeasurementsDashed'
        ) {
            return false;
        }
    }

    switch (type) {
        case 'wallMeasurement': {
            // Wall measurement arrows: show when not dragging, respect direction-based hiding and ruler
            let shouldHideBasedOnDirection = false;
            if (selectedCabinetGroup) {
                const currentDirection =
                    selectedCabinetGroup.userData.currentDirection;
                if (currentDirection && typeof currentDirection === 'string') {
                    shouldHideBasedOnDirection = shouldHideArrowGroup(
                        arrowId,
                        currentDirection
                    );
                }
            }
            return preventVisibilityWhenDragging(
                isDragging,
                !shouldHideBasedOnDirection && showArrows
            );
        }

        case 'cabinetDimension': {
            // Cabinet dimension arrows: only show for selected cabinet when not dragging and ruler is on
            const isSelectedCabinetArrow =
                selectedCabinetGroup &&
                arrow.userData?.id === selectedCabinetGroup.userData.id;
            return preventVisibilityWhenDragging(
                isDragging,
                Boolean(isSelectedCabinetArrow && showArrows)
            );
        }

        case 'cabinetMovementArrows': {
            // Movement arrows: show only when movement mode is active AND not dragging
            const isActiveGroupArrow =
                selectedCabinetGroup &&
                arrow.userData?.id === selectedCabinetGroup.userData.id;
            const isMovementMode =
                Boolean(isActiveGroupArrow) &&
                Boolean(selectedCabinetGroup?.userData.isMovementMode);
            return preventVisibilityWhenDragging(isDragging, isMovementMode);
        }

        case 'distanceWallMeasurement': {
            // Distance wall measurements: show when not dragging AND movement mode is active
            const isActiveGroupArrow =
                selectedCabinetGroup &&
                arrow.userData?.id === selectedCabinetGroup.userData.id;
            const isMovementMode =
                Boolean(isActiveGroupArrow) &&
                Boolean(selectedCabinetGroup?.userData.isMovementMode);
            return preventVisibilityWhenDragging(isDragging, isMovementMode);
        }

        case 'distanceWallMeasurementsDashed': {
            // Floor dashed guides: show for the selected cabinet when the ruler is on (same idea as
            // cabinet dimension overlays). Solid blue distance labels still require movement mode.
            if (!selectedCabinetGroup) {
                return false;
            }
            const isActiveGroupArrow =
                arrow.userData?.id === selectedCabinetGroup.userData.id;
            const cabinetTargetUuid = (
                arrow.userData as {cabinetTargetUuid?: string}
            ).cabinetTargetUuid;
            const matchesMesh =
                !cabinetTargetUuid ||
                cabinetTargetUuid === selectedCabinetGroup.uuid;
            const shouldShow =
                Boolean(isActiveGroupArrow) && matchesMesh && showArrows;
            return preventVisibilityWhenDragging(isDragging, shouldShow);
        }

        default:
            return true;
    }
};

/**
 * Helper function to dispose movement arrows when dragging starts
 * @param {boolean} isDragging - Whether dragging is active
 * @param {CabinetMesh | null} selectedCabinetGroup - The currently selected cabinet group
 * @param {React.MutableRefObject<THREE.Group[]>} arrowGroupRefs - Reference to arrow groups
 * @return {void}
 */
export const disposeMovementArrowsOnDrag = (
    isDragging: boolean,
    selectedCabinetGroup: CabinetMesh | null,
    arrowGroupRefs: React.MutableRefObject<
        THREE.Group<THREE.Object3DEventMap>[]
    >
): void => {
    if (!isDragging || !selectedCabinetGroup) {
        return;
    }

    const isMovementMode = Boolean(
        selectedCabinetGroup.userData.isMovementMode
    );
    if (!isMovementMode) {
        return;
    }

    const arrowGroups = arrowGroupRefs.current;
    const arrowsToRemove: typeof arrowGroups = [];
    const selectedCabinetId = selectedCabinetGroup.userData.id;

    for (const arrow of arrowGroups) {
        if (!arrow) continue;

        const isMovementArrow =
            arrow.name === 'cabinetMovementArrows' &&
            arrow.userData?.id === selectedCabinetId;

        if (isMovementArrow) {
            disposeObjectResources(arrow);

            if (arrow.parent) {
                arrow.parent.remove(arrow);
            }

            arrow.userData.isDisposed = true;
            arrowsToRemove.push(arrow);
        }
    }

    // Remove collected arrows from refs
    arrowsToRemove.forEach((arrow) => {
        const index = arrowGroups.indexOf(arrow);
        if (index >= 0) {
            arrowGroups.splice(index, 1);
        }
    });
};

/**
 * Helper function to check if a line is a dashed line
 * @param {THREE.Line} line - The line to check
 * @return {boolean} Whether the line is a dashed line
 */
export const isDashedLine = (line: THREE.Line): boolean => {
    const dashedLineIds = [
        'leftLineTop-wall',
        'leftLine2',
        'rightLine-wall',
        'rightLine2-wall',
        'leftVerticalLine-wall',
        'rightVerticalLine-wall',
    ];
    return (
        dashedLineIds.includes(line.userData.id as string) ||
        (line.material as THREE.LineDashedMaterial)?.isLineDashedMaterial ===
            true
    );
};
